/**
 * OrderRow — shared <tr> row for admin order tables.
 *
 * Used by:
 *  - `BillingOrders.tsx` (global admin list — Tenant column visible)
 *  - `TenantDetails.tsx` OrdersTab (`hideTenantColumn={true}`, AC-C9)
 *
 * Preserves prior behaviour:
 *  - `role="button"`, `tabIndex={0}`, Enter/Space keyboard activation.
 *  - `data-testid="order-row-${id}"` for test selectors.
 *
 * @see docs/specifications/2026-05-14-tenant-detail-billing-tabs/spec.md AC-O5
 */
import React from "react";
import {
  OrderStatusBadge,
  type OrderStatus,
} from "../../../../components/billing/OrderStatusBadge";
import { PAYMENT_METHOD_LABELS } from "../utils/orderFormatters";
import type { BillingOrder } from "../../../../types/adminBilling";

const orderCurrencyFormatter = new Intl.NumberFormat("pl-PL", {
  style: "currency",
  currency: "PLN",
  currencyDisplay: "code",
  maximumFractionDigits: 2,
});

export interface OrderRowProps {
  order: BillingOrder;
  hideTenantColumn?: boolean;
  onSelectDetail: (id: string) => void;
  rowRef?: (el: HTMLTableRowElement | null) => void;
}

export const OrderRow: React.FC<OrderRowProps> = ({
  order,
  hideTenantColumn,
  onSelectDetail,
  rowRef,
}) => {
  return (
    <tr
      ref={rowRef}
      className="hover:bg-neutral-50 cursor-pointer"
      tabIndex={0}
      role="button"
      aria-label={`Szczegóły zamówienia ${order.orderNumber}`}
      onClick={() => onSelectDetail(order.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelectDetail(order.id);
        }
      }}
      data-testid={`order-row-${order.id}`}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="font-medium text-neutral-900 font-mono text-sm">
          {order.orderNumber}
        </span>
      </td>
      {!hideTenantColumn && (
        <td className="px-6 py-4 whitespace-nowrap">
          {order.tenantName ? (
            <span className="text-sm text-neutral-800">{order.tenantName}</span>
          ) : (
            <span className="text-xs font-mono text-neutral-500">
              {order.tenantId.slice(0, 8)}...
            </span>
          )}
        </td>
      )}
      <td className="px-6 py-4 whitespace-nowrap">
        <OrderStatusBadge status={order.status as OrderStatus} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-800">
        {orderCurrencyFormatter.format(order.totalNetCents / 100)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
        {(order.paymentMethod && PAYMENT_METHOD_LABELS[order.paymentMethod]) ??
          order.paymentMethod ??
          "—"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
        {new Date(order.createdAt).toLocaleDateString("pl-PL")}
      </td>
      <td
        className="px-6 py-4 whitespace-nowrap text-right text-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => onSelectDetail(order.id)}
          className="text-primary-600 hover:text-primary-800 font-medium"
        >
          Szczegóły
        </button>
      </td>
    </tr>
  );
};

export interface OrderTableHeadProps {
  hideTenantColumn?: boolean;
}

export const OrderTableHead: React.FC<OrderTableHeadProps> = ({
  hideTenantColumn,
}) => {
  const thClass =
    "px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider";
  return (
    <thead className="bg-neutral-50">
      <tr>
        <th scope="col" className={thClass}>
          Numer
        </th>
        {!hideTenantColumn && (
          <th scope="col" className={thClass}>
            Tenant
          </th>
        )}
        <th scope="col" className={thClass}>
          Status
        </th>
        <th scope="col" className={thClass}>
          Kwota netto
        </th>
        <th scope="col" className={thClass}>
          Metoda płatności
        </th>
        <th scope="col" className={thClass}>
          Data
        </th>
        <th
          scope="col"
          className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider"
        >
          Akcje
        </th>
      </tr>
    </thead>
  );
};
